import { useEffect, useRef, useState } from 'react'
import { toast } from '@/stores/useToastStore'

interface VersionInfo {
  buildId?: string
  commit?: string
  buildNumber?: string
  date?: string
}

export function useVersionCheck() {
  const [version, setVersion] = useState<string | null>(null)
  const currentVersionRef = useRef<string | null>(null)
  const toastShown = useRef(false)

  useEffect(() => {
    const checkVersion = async (isInitial = false) => {
      try {
        const response = await fetch(`/version.json?t=${Date.now()}`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache',
          },
        })
        if (!response.ok) return // Silently ignore if file doesn't exist (e.g. in dev environment)

        const data: VersionInfo = await response.json()
        const versionIdentifier = data.commit || data.buildId || data.buildNumber

        if (!versionIdentifier) return

        if (isInitial) {
          currentVersionRef.current = versionIdentifier
          setVersion(versionIdentifier)
        } else if (
          currentVersionRef.current &&
          currentVersionRef.current !== versionIdentifier &&
          !toastShown.current
        ) {
          toastShown.current = true

          toast.info(
            <div className="flex flex-col gap-2">
              <span className="font-semibold">Update Available</span>
              <span className="text-xs text-gray-600">
                A new version of the application has been deployed.
              </span>
              <button
                onClick={async () => {
                  // Attempt to clear CacheStorage if present
                  if ('caches' in window) {
                    try {
                      const cacheNames = await caches.keys()
                      await Promise.all(cacheNames.map(name => caches.delete(name)))
                    } catch (e) {
                      console.error('Failed to clear caches', e)
                    }
                  }

                  // Force a true hard refresh by appending a cache-busting timestamp to the URL
                  const currentUrl = new URL(window.location.href)
                  currentUrl.searchParams.set('update', Date.now().toString())
                  window.location.href = currentUrl.toString()
                }}
                className="mt-1 self-start rounded bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 focus:outline-none transition-colors cursor-pointer shadow-sm"
              >
                Refresh Now
              </button>
            </div>,
            0 // 0 means do not auto-remove the toast
          )
        }
      } catch (error) {
        console.error('Failed to check application version:', error)
      }
    }

    // Run initial check
    checkVersion(true)

    // Run check every 5 minutes (300,000 ms)
    const interval = setInterval(() => {
      checkVersion(false)
    }, 5 * 60 * 1000)

    return () => clearInterval(interval)
  }, [])

  return version
}
